import { PopoverContent, PopoverTrigger } from '@radix-ui/react-popover';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, EllipsisVertical, Palette, Save, SettingsIcon, Workflow } from 'lucide-react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
    Control,
    SubmitHandler,
    useForm,
    UseFormGetValues,
    UseFormRegister,
    UseFormSetValue,
    UseFormWatch,
} from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslate } from 'sbx-crm-menu-ui';
import { toast } from 'sonner';
import { DeniedComponent } from 'src/App';
import { Popover } from 'src/components/ui/popover';
import { findAllComponentService } from 'src/services/ComponentService';
import { AiAgentV3, Flow, InfoWatch } from 'src/types/AiAgentV3';
import { ComponentType } from 'src/types/ComponentType';
import { AI_AGENT_V2_CREATE, AI_AGENT_V2_EDIT } from 'src/types/Permission';
import { capitalize, grantPermission, watchAndReplaceValues } from 'src/utils';
import { defaultColorsBotV3, defaultSizesBotV3, formatCss } from 'src/utils/botGenerator/formatCss';
import { defaultValuesTextBotV3 } from 'src/utils/botGenerator/formatScript2';
import { AppAgentContext } from '../../../context/ContextProvider';
import useIgnoreRenderEffect from '../../../hooks/useIgnoreRenderEffect';
import {
    getAgentByIdServiceV3,
    getFlowAgentListService,
    getReports,
    upsertAgentServiceV3,
} from '../../../services/AgentService';
import { appActions, DataStates } from '../../../store';
import { ReportResponse } from '../../../types/AiAgentV2';
import { ModalType } from '../../../types/Modal';
import { formatInitBot } from '../../../utils/botGenerator/formatScript2';
import ButtonComponent from '../../shared/ButtonComponent';
import InteractiveTabsComponent, { InteractiveTab } from '../../shared/InteractiveTabsComponent';
import { LoadingComponent } from '../../shared/LoadingComponent';
import NavbarBreadcrumbV2 from '../../shared/NavbarBreadcrumbV2';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Button } from '../../ui/button';
import AgentDeployV3Component from './agentSetupComponent/AgentDeployV3Component';
import { AgentFlowComponent } from './agentSetupComponent/AgentFlowComponent';
import AgentIdentityComponent from './agentSetupComponent/AgentIdentityComponent';
import AgentPopUpColorsComponent from './agentSetupComponent/AgentPopUpColorsComponent'; // import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';

interface FormContextType {
    watch: UseFormWatch<AiAgentV3>;
    setValue: UseFormSetValue<AiAgentV3>;
    getValues: UseFormGetValues<AiAgentV3>;
    control: Control<AiAgentV3>;
    reportsOptions: ReportResponse[];
    getAgent: () => void;
    register: UseFormRegister<AiAgentV3>;
    componentCmsList: { value: string; label: string; data: ComponentType }[];
    flows: Flow[];
    oldExtraConfig: Record<string, any>;
}

const PROCESS_LENGTH = 2;

// Creamos el contexto con un valor inicial vacío (será sobrescrito luego)
export const AgentSetupContextV3 = createContext<FormContextType>({
    watch: () => ({}) as any,
    setValue: () => {},
    getValues: () => ({}) as any,
    control: {} as Control<AiAgentV3>,
    reportsOptions: [],
    getAgent: () => {},
    register: {} as UseFormRegister<AiAgentV3>,
    componentCmsList: [],
    flows: [],
    oldExtraConfig: {},
});

const infoWatch: InfoWatch = {
    closed_welcome: {
        className: '.fbw-closed-welcome',
    },
    header_title: {
        className: '.fbw-name span',
    },
    header_subtitle: {
        className: '.fbw-desc span',
    },
    opts_expand: {
        className: '.fbw-opts-item.fbw-opts-expand',
    },
    opts_finalize: {
        className: '.fbw-opts-item.fbw-opts-finalize',
    },
    q1_question: {
        className: '.fbw-msg-bot .fbw-msg-body .parr_1',
    },
    q1_panel_description: {
        className: '.fbw-msg-bot .fbw-msg-body .parr_3',
    },
    q1_placeholder: {
        className: '.fbw-input.fbw-input-message',
        mode: 'placeholder',
    },
    q1_error_message: {
        className: '.fbw-msg-bot .fbw-msg-body .one_mess',
    },
    q2_question: {
        className: '.fbw-msg-bot .fbw-msg-body .parr_1',
        index: 2,
    },
    q2_panel_description: {
        className: '.fbw-msg-bot .fbw-msg-body .parr_3',
        index: 2,
    },
    q2_placeholder: {
        className: '.fbw-input.fbw-input-message',
        mode: 'placeholder',
        beforeClassName: '.fbw-msg-bot .fbw-msg-body .parr_1',
        indexPlaceholder: 2,
    },
    input_placeholder: {
        className: '.fbw-input.fbw-input-message',
        mode: 'placeholder',
        beforeClassName: '.fbw-msg-bot .fbw-msg-body .parr_1',
        indexPlaceholder: 3,
        isNullBefore: true,
    },
    // q2_error_message: {
    //     className: '.fbw-input.fbw-input-message',
    //     mode: 'title',
    //     beforeClassName: '.fbw-msg-bot .fbw-msg-body .parr_1',
    //     indexPlaceholder: 2,
    // },
    q3_question: {
        className: '.fbw-msg-bot .fbw-msg-body .parr_1',
        index: 3,
    },
    q3_panel_description: {
        className: '.fbw-msg-bot .fbw-msg-body .parr_3',
        index: 3,
    },
    terms_link_label: {
        className: '.fbw-terms-link',
    },
    terms_accept_button: {
        className: '.fbw-btn.fbw-btn-accept-terms',
    },
    header_options_title: {
        className: '.fbw-opts-btn',
        mode: 'title',
    },
    header_close_title: {
        className: '.fbw-close',
        mode: 'title',
    },
    send_title: {
        className: '.fbw-btn.fbw-btn-send',
        mode: 'title',
    },
    send_aria:  {
        className: '.fbw-btn.fbw-btn-send',
        mode: 'aria-label'
    },
    powered_by: {
        className: '.fbw-powered-label',
    },
    tool_call_title: {
        className: '#call-assessor-btn',
        bothModes: true,
        mode: 'title'
    },
    tool_mic_title: {
        className: '#mic-btn',
        mode: 'title'
    },
    suggestions_title: {
        className: '.fbw-tool-btn.fbw-suggest-btn',
        mode: 'title'
    },
    welcome_template: {
        className: '.fbw-msg.fbw-msg-system .fbw-msg-text .fbw-msg-body',
    },
    toggle_title: {
        className: '.fbw-toggle',
        mode: 'title'
    }
};

export default function AgentSetupV3Component() {
    const navigate = useNavigate();
    const { id } = useParams();
    // const [loading, setLoading] = useState(State.IDLE);
    const [searchParams, setSearchParams] = useSearchParams();
    const [progressBar, setProgressBar] = useState<null | number>(null);
    const [oldExtraConfig, setOldExtraConfig] = useState<Record<string, any>>({});
    // const [agentClass, setAgentClass] = useState(new AiAgentV2({}));
    // const [isAvailablePreview, setIsAvailablePreview] = useState(false);

    const { data, isLoading, refetch, isRefetching } = useQuery({
        queryKey: ['agent_detail', id],
        queryFn: async () => {
            if (!!id && id !== 'create') {
                return await getAgentByIdServiceV3(id as string);
            }
            return null;
        },
        // enabled: !!id && id !== 'create',
    });

    const {
        state: {
            session: { permissions, domainName, origin },
        },
        dispatch,
    } = useContext(AppAgentContext);
    const [nameProject, setNameProject] = useState('');

    const [allowCreate, allowEdit] = useMemo(() => {
        return [grantPermission(AI_AGENT_V2_CREATE, permissions), grantPermission(AI_AGENT_V2_EDIT, permissions)];
    }, [permissions]);

    const { data: reportsOptions } = useQuery({
        queryKey: ['reports'],
        queryFn: async () =>
            await getReports()
                .then((res) => {
                    if (res.success) {
                        return res.items;
                    }
                    return [];
                })
                .catch((err) => err),
    });

    const { data: componentCmsList } = useQuery({
        queryKey: ['component_list'],
        queryFn: async () =>
            await findAllComponentService()
                .then((res) => {
                    if (res.success) {
                        return (
                            res.items?.map((item) => {
                                let id = '';
                                if (item.file_key) id = `source_file_${item.file_key}`;
                                else id = `source_model_${item._KEY}`;

                                return {
                                    value: id,
                                    data: item,
                                    label: capitalize(item.name),
                                };
                            }) ?? []
                        );
                    }
                    return [];
                })
                .catch((err) => err),
    });

    const { data: flows } = useQuery({
        queryKey: ['flow'],
        queryFn: async () =>
            await getFlowAgentListService()
                .then((res) => {
                    if (res.success) {
                        return res.items;
                    }
                    return [] as Flow[];
                })
                .catch((err) => err),
    });

    const { t: agent } = useTranslate('agent');
    const { t: common } = useTranslate('common');
    const { t: customMessage } = useTranslate('custom-message');

    const { control, getValues, setValue, watch, handleSubmit, register } = useForm<AiAgentV3>({
        defaultValues: {
            name: '',
            domain: '',
            greeting: '',
            look_and_feel_chat: {
                suggested_message: [],
                avatar: '',
                agent_name: '',
                circular_alignment: null,
                privacy_policy_url: null,
                extra_config: { ...defaultValuesTextBotV3, ...defaultColorsBotV3, ...defaultSizesBotV3 },
            },
            look_and_feel_voice: { agent_name: '' },
            look_and_feel_whatsapp: { agent_name: '' },
            privacy: {
                public: false,
                white_list_ips: [],
                white_list_countries: [],
                white_list_source: [],
                black_list_ips: [],
                black_list_countries: [],
                black_list_source: [],
                authentication_type: null,
                authentication_config: [],
                policies: [],
            },
            avatar: '',
            main_model: '',
        },
    });

    useEffect(() => {
        if (data?.item) {
            setAllDataBot(data.item);
        }
    }, [data]);

    function setAllDataBot(data: AiAgentV3) {
        if (data?.id) {
            if (data.look_and_feel_chat) {
                if (data.look_and_feel_chat?.extra_config) {
                    try {
                        data.look_and_feel_chat.extra_config = JSON.parse(data.look_and_feel_chat.extra_config);
                    } catch (e) {
                        console.error('Error parsing extra_config:', e);
                    }
                } else {
                    data.look_and_feel_chat.extra_config = {
                        ...defaultValuesTextBotV3,
                        ...defaultColorsBotV3,
                        ...defaultSizesBotV3,
                    };
                }
                setValue('look_and_feel_chat', {
                    ...data.look_and_feel_chat,
                    suggested_message: data.look_and_feel_chat?.suggested_message || [],
                } as any);
                setOldExtraConfig(data.look_and_feel_chat.extra_config);
            }

            // if (data.privacy) {
            //     setValue('privacy', {
            //         public: data.privacy?.public ?? false,
            //         white_list_ips: data.privacy?.white_list_ips || [],
            //         white_list_countries: data.privacy?.white_list_countries || [],
            //         white_list_source: data.privacy?.white_list_source || [],
            //         black_list_ips: data.privacy?.black_list_ips || [],
            //         black_list_countries: data.privacy?.black_list_countries || [],
            //         black_list_source: data.privacy?.black_list_source || [],
            //         authentication_type: data.privacy?.authentication_type || null,
            //         authentication_config: data.privacy?.authentication_config || [],
            //         policies: data.privacy?.policies || [],
            //     });
            // }

            if (data.look_and_feel_voice) {
                setValue('look_and_feel_voice', {
                    agent_name: data.look_and_feel_voice.agent_name || '',
                });
            }

            if (data.look_and_feel_whatsapp) {
                setValue('look_and_feel_whatsapp', {
                    agent_name: data.look_and_feel_whatsapp.agent_name || '',
                });
            }
            setNameProject(data.name ?? '');
            setValue('name', data.name ?? '');
            setValue('domain', data.domain ?? '');
            setValue('greeting', data.greeting ?? '');
            setValue('greeting_en', data.greeting_en ?? '');
            setValue('avatar', data.avatar ?? '');
            setValue('main_model', data.main_model);
        }
    }

    const percentage =
        progressBar !== null ? (progressBar !== 0 ? Math.floor((progressBar / PROCESS_LENGTH) * 100) : 0) : null;

    const onSubmit: SubmitHandler<AiAgentV3> = async (data) => {
        const submittedData = {
            ...data,
        };

        if (!data.name || !data.main_model) {
            searchParams.set('mode', 'agent');
            setSearchParams(searchParams);
            toast(
                <div className="flex flex-col">
                    <b>{customMessage('should_complete_following_fields')}:</b>
                    <ul className="list-disc px-3">{!data.name && <li>{common('name')}</li>}</ul>
                    <ul className="list-disc px-3">{!data.main_model && <li>{common('flow')}</li>}</ul>
                </div>,
                { richColors: true }
            );
            return;
        }

        dispatch(
            appActions.openModal({
                type: ModalType.CONFIRMATION,
                data: {
                    title: `${common(id !== 'create' ? 'update' : 'save')} ${agent('agent')}`,
                    titleClassName: 'text-2xl font-bold text-purple-800',
                    description: `${customMessage(id !== 'create' ? 'warn-update' : 'warn-save')} ${common(id !== 'create' ? 'update' : 'save')} ${agent('agent')}?`,
                    className:
                        '!bg-gradient-to-br !from-purple-50 !to-pink-50 !rounded-3xl !shadow-2xl w-full max-w-md overflow-hidden',
                    cancelButtonClassName: 'border-pink-500 btn text-pink-600 hover:bg-pink-50 w-[200px]',
                    confirmButtonClassName:
                        'bg-gradient-to-r btn from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white w-[200px]',
                    state: DataStates.AGENT,
                    async onConfirm() {
                        if (id && id !== 'create') {
                            submittedData.id = parseInt(id);
                        }
                        dispatch(appActions.closeModal(ModalType.CONFIRMATION));
                        setProgressBar(0);
                        const versionBot = format(new Date(), 'yyyy-MM-dd HH:mm:ss');

                        submittedData.look_and_feel_chat.extra_config = JSON.stringify(
                            data.look_and_feel_chat.extra_config
                                ? { ...data.look_and_feel_chat.extra_config, version: versionBot }
                                : {
                                      ...defaultValuesTextBotV3,
                                      ...defaultColorsBotV3,
                                      ...defaultSizesBotV3,
                                      version: versionBot,
                                  }
                        );
                        const response = await upsertAgentServiceV3(submittedData);

                        if (response?.success) {
                            setProgressBar(2);
                            // reset();

                            if (!id || id === 'create') {
                                endProcessForm();
                                setProgressBar(null);
                                navigate(`/agent_setup_v3/${response.item.id}`);
                            } else {
                                endProcessForm();
                                setProgressBar(null);
                                setAllDataBot(response.item);
                                // refetch();
                            }
                            // await initBotV3();
                        } else {
                            toast.error(agent(id ? 'agent_updated_error' : 'agent_created_error'));
                        }
                    },
                    onClose() {
                        dispatch(appActions.closeModal(ModalType.CONFIRMATION));
                    },
                },
            })
        );
    };

    const endProcessForm = () => {
        toast.success(agent(id ? 'agent_updated_successfully' : 'agent_created_successfully'));
    };

    const tabs: InteractiveTab[] = useMemo(
        () => [
            // { id: 'agent', label: agent('agent'), color: '#E11D48', icon: <User />, component: <AgentComponent /> },
            {
                id: 'identity',
                label: agent('identity'),
                color: '#FF9F1C',
                icon: <Palette />,
                component: <AgentIdentityComponent />,
            },
            {
                id: 'flow',
                label: agent('flow') || 'Flow',
                color: '#9B5DE5',
                icon: <Workflow />,
                component: <AgentFlowComponent />,
            },
            {
                id: 'deploy',
                label: agent('deploy'),
                color: '#00B4D8',
                icon: <SettingsIcon />,
                component: <AgentDeployV3Component />,
            },
        ],
        []
    );

    async function initBotV3() {
        const container = document.querySelector('.preview-bot');
        if (container) {
            container.firstChild?.remove();
            const div = document.createElement('div');
            div.id = 'container-bot-id';
            div.className = 'relative';
            container.appendChild(div);
        }
        const scriptExist = document.getElementById('container-bot-id-script');
        if (scriptExist) scriptExist.remove();
        const dataBot = data?.item;
        // const dataBot = await getAgentData(process.env.REACT_APP_BASE_URL || window.location.origin, id || '0');
        const script = formatInitBot(
            {
                baseUrl: process.env.REACT_APP_BASE_URL || window.location.origin,
                agentId: id,
                withMicrophone: true,
                withAssessor: true,
                withAttachment: true,
                withInitialMessage: true,
                urlCss: null,
                urlFiles: origin.concat('/www/', domainName, '/ai-agent/'),
                dataBot,
                policy_terms_url: dataBot?.look_and_feel_chat?.privacy_policy_url ?? '',
            },
            'container-bot-id'
        );

        setCss(formatCss(dataBot?.look_and_feel_chat?.extra_config, Number(id)));

        const scriptElement = document.createElement('script');
        scriptElement.id = 'container-bot-id-script';
        scriptElement.innerHTML = script;
        document.body.appendChild(scriptElement);
    }

    useIgnoreRenderEffect(async () => {
        if (data?.success) {
            await initBotV3();
        }
    }, [data]);

    const [css, setCss] = useState('');

    useEffect(() => {
        const subscriber = watch((value, info) => {
            if (
                info.name?.includes('look_and_feel_chat.extra_config.') ||
                info.name === 'look_and_feel_chat.extra_config' ||
                info.name === 'avatar'
            ) {

                watchAndReplaceValues(
                    info.name || '',
                    (info.values as any).look_and_feel_chat.extra_config,
                    infoWatch,
                    getValues('look_and_feel_chat.agent_name')
                );

                const extraConfig = value.look_and_feel_chat?.extra_config;
                const css = formatCss(extraConfig, Number(id));
                setCss(css);
            }
        });

        return () => {
            subscriber.unsubscribe();
        };
    }, [watch]);

    if (isLoading || isRefetching) {
        return <LoadingComponent />;
    }

    if (id === 'create' && !allowCreate) {
        return <DeniedComponent />;
    }

    return (
        <div
            className={`grid grid-cols-1 xl:grid-cols-[2fr_auto] min-h-screen bg-gradient-to-br from-pink-100 to-indigo-100 p-8`}
        >
            <div className="absolute opacity-0" dangerouslySetInnerHTML={{ __html: `<style> ${css} </style>` }}></div>
            <div className="max-w-[1920px] w-full mx-auto p-4 space-y-4">
                <NavbarBreadcrumbV2
                    routes={[
                        {
                            label: agent('agent_list'),
                            route: '/agent_list_v3',
                        },
                        { label: `${id === 'create' ? agent('create_new_agent') : `${agent('agent')} ${id}`}` },
                    ]}
                />

                <AgentSetupContextV3.Provider
                    value={{
                        getValues,
                        control,
                        setValue,
                        watch,
                        reportsOptions,
                        register,
                        getAgent: refetch,
                        componentCmsList,
                        flows,
                        oldExtraConfig,
                    }}
                >
                    <div className="flex w-full h-full ">
                        <div className="flex flex-col w-2/3 bg-white rounded-lg shadow-lg py-6 px-3 border ">
                            <div className="flex flex-col md:flex-row justify-between md:items-center mb-6 gap-3">
                                <div className="flex items-center gap-2">
                                    <Button
                                        onClick={() => {
                                            navigate('/agent_list_v3');
                                        }}
                                        className="bg-transparent hover:bg-purple-100 text-purple-600 p-2 rounded-full transition-colors duration-300"
                                    >
                                        <ArrowLeft size={24} />
                                    </Button>
                                    <h1 className="text-3xl sm:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 flex items-center gap-2">
                                        {getValues('avatar') && (
                                            <Avatar>
                                                <AvatarImage src={getValues('avatar')} alt={getValues('name')} />
                                                <AvatarFallback>{nameProject[0]}</AvatarFallback>
                                            </Avatar>
                                        )}
                                        {nameProject ?? agent('new_agent')}
                                    </h1>
                                </div>
                                <div className="flex items-center gap-2">
                                    {((id === 'create' && allowCreate) || (id !== 'create' && allowEdit)) && (
                                        <ButtonComponent
                                            icon={<Save className="w-[16px] h-[16px]" />}
                                            loading={progressBar !== null}
                                            className="flex items-center gap-1 w-full sm:w-auto bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold py-2 px-6 rounded-full transition-all duration-300 transform hover:scale-105"
                                            label={`${common(id !== 'create' ? 'update' : 'save')} ${agent('agent')}`}
                                            disabled={progressBar !== null}
                                            onClick={() => {
                                                handleSubmit(onSubmit)();
                                            }}
                                        />
                                    )}
                                    {data?.item.id && (
                                        <Popover>
                                            <PopoverTrigger>
                                                <EllipsisVertical className="w-[20px] h-[20px]" />
                                            </PopoverTrigger>
                                            <PopoverContent className="bg-white shadow-xl rounded-lg w-[180px] mr-6 p-1 xl:mr-0 z-[50]">
                                                <div className="flex flex-col gap-1">
                                                    <ButtonComponent
                                                        icon={<ArrowRight className="w-[16px] h-[16px]" />}
                                                        className="w-full flex text-black  bg-white items-center justify-start gap-2 px-2 h-8 text-sm rounded-sm hover:bg-gray-100 transition-colors text-left"
                                                        label={`Go to markdown`}
                                                        onClick={() => {
                                                            navigate(`/mark-down/chat/${data?.item.id}`);
                                                        }}
                                                    />
                                                    <ButtonComponent
                                                        icon={<ArrowRight className="w-[16px] h-[16px]" />}
                                                        className="w-full flex text-black bg-white items-center justify-start gap-2 px-2 h-8 text-sm rounded-sm hover:bg-gray-100 transition-colors text-left"
                                                        label={`Go to preview script`}
                                                        onClick={() => {
                                                            window.open(
                                                                `/ai-studio/#/bot-script/${data?.item.id}`,
                                                                '_blank'
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            </PopoverContent>
                                        </Popover>
                                    )}
                                </div>
                            </div>

                            <InteractiveTabsComponent
                                tabs={tabs.filter((tab) => !(id === 'create' && tab.id === 'deploy'))}
                            />
                        </div>
                        <div className="preview-bot h-full w-1/3  relative flex justify-end items-center  ">
                            {/*create style here for preview bot*/}
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: `<style>
                                /* Inline styles for preview bot roles panel (mirrors globalStyles.css) */
 
                              </style>`,
                                }}
                            />

                            <div id="container-bot-id" className="relative" />
                        </div>
                    </div>
                </AgentSetupContextV3.Provider>
            </div>

            <div className="mt-36 xl:flex hidden">
                {/* <AgentLayoutComponentV2
                    id="agent_layout_v2"
                    agentId={id !== 'create' ? id : undefined}
                    messages={[]}
                    agent={agentClass}
                    isAvailablePreview={!(!!watch('main_model') && watch('main_model').length > 0)}
                    testSkill
                /> */}
            </div>
            {percentage !== null && (
                <div className="w-screen z-[30] flex justify-center items-center h-dvh bg-black-transparent fixed top-0 left-0">
                    <div className="p-10 min-w-[700px] bg-white shadow  rounded">
                        <motion.div
                            className="h-4 rounded  bg-gradient-to-r from-purple-600 to-pink-600 mb-6"
                            initial={{ width: 0 }}
                            animate={{ width: `${percentage}%` }}
                            transition={{ duration: 0.5, ease: 'easeInOut' }}
                        />
                        <div className="flex justify-between items-center">
                            <div className="text-lg font-bold">{agent('agent_saving_data')}</div>
                            <div className="text-lg font-bold">{percentage}%</div>
                            <div className="text-lg font-bold">
                                {progressBar}/{PROCESS_LENGTH}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <AgentPopUpColorsComponent watch={watch} setValue={setValue} oldExtraConfig={oldExtraConfig} />
        </div>
    );
}
